// layerOrderingHelpers.ts - Helper functions for managing layer ordering

import { Settings } from '@ais-ui/websockets';
import { LayerOrder } from './types';

export const addToLayerOrdering = (
  settings: Settings,
  layerType: string,
  layerId: string,
  position?: number,
) => {
  const currentOrdering = [
    ...(settings['ribbonPlugin.settings']?.layer_ordering?.layers || []),
  ];
  const newLayer: LayerOrder = { type: layerType, id: layerId, enabled: true };

  if (position !== undefined) {
    currentOrdering.splice(position, 0, newLayer);
  } else {
    currentOrdering.push(newLayer);
  }

  return {
    ...settings,
    'ribbonPlugin.settings': {
      ...settings['ribbonPlugin.settings'],
      layer_ordering: {
        version: 1,
        layers: currentOrdering,
      },
    },
  };
};

export const removeFromLayerOrdering = (
  settings: Settings,
  layerId: string,
) => {
  const currentOrdering: LayerOrder[] =
    settings['ribbonPlugin.settings']?.layer_ordering?.layers || [];

  return {
    ...settings,
    'ribbonPlugin.settings': {
      ...settings['ribbonPlugin.settings'],
      layer_ordering: {
        version: 1,
        layers: currentOrdering.filter((layer) => layer.id !== layerId),
      },
    },
  };
};

// Add layer just below the ships layer (ships layer stays on top visually)
export const addLayerBelowShips = (
  settings: Settings,
  layerType: string,
  layerId: string,
) => {
  const currentOrdering: LayerOrder[] =
    settings['ribbonPlugin.settings']?.layer_ordering?.layers || [];

  // Find the ships layer index
  const shipsIndex = currentOrdering.findIndex(
    (layer) => layer.type === 'vessels',
  );

  if (shipsIndex === -1) {
    // No ships layer found, add the new layer first, then ships
    // This way ships will be on top visually
    const newOrdering = [
      { type: layerType, id: layerId, enabled: true },
      { type: 'vessels', id: 'ships-layer', enabled: true },
    ];

    return {
      ...settings,
      'ribbonPlugin.settings': {
        ...settings['ribbonPlugin.settings'],
        layer_ordering: {
          version: 1,
          layers: newOrdering,
        },
      },
    };
  } else {
    // Insert the new layer BEFORE the ships layer (at shipsIndex position)
    // This pushes ships to a higher index, making ships appear on top
    return addToLayerOrdering(settings, layerType, layerId, shipsIndex);
  }
};

// Update favorite shapes and layer ordering together
export const addShapeToFavoritesWithOrdering = (
  settings: Settings,
  shapeId: string,
) => {
  // First, add to favorite shapes
  const updatedSettings = {
    ...settings,
    'ribbonPlugin.settings': {
      ...settings['ribbonPlugin.settings'],
      favorite_shapes: {
        enabled: false,
        shapes: [
          ...(settings['ribbonPlugin.settings']?.favorite_shapes?.shapes || []),
          shapeId,
        ],
      },
    },
  };

  // Then add to layer ordering below ships
  return addLayerBelowShips(updatedSettings, 'shape', shapeId);
};

// Remove favorite shape and from layer ordering
export const removeShapeFromFavoritesWithOrdering = (
  settings: Settings,
  shapeId: string,
) => {
  // First, remove from favorite shapes
  const updatedSettings = {
    ...settings,
    'ribbonPlugin.settings': {
      ...settings['ribbonPlugin.settings'],
      favorite_shapes: {
        enabled: false,
        shapes:
          settings['ribbonPlugin.settings']?.favorite_shapes?.shapes?.filter(
            (id: string) => id !== shapeId,
          ) || [],
      },
    },
  };

  // Then, remove from layer ordering
  return removeFromLayerOrdering(updatedSettings, shapeId);
};

// Helper functions for WMS layers
export const addWmsToFavoritesWithOrdering = (
  settings: Settings,
  wmsLayerId: string,
) => {
  return addLayerBelowShips(settings, 'wms', wmsLayerId);
};

export const removeWmsFromFavoritesWithOrdering = (
  settings: Settings,
  wmsLayerId: string,
) => {
  return removeFromLayerOrdering(settings, wmsLayerId);
};

// Helper functions for Vector Tile layers
export const addVectorTileToFavoritesWithOrdering = (
  settings: Settings,
  vectorTileId: string,
) => {
  return addLayerBelowShips(settings, 'vectortile', vectorTileId);
};

export const removeVectorTileFromFavoritesWithOrdering = (
  settings: Settings,
  vectorTileId: string,
) => {
  return removeFromLayerOrdering(settings, vectorTileId);
};

export const isVectorTileShownOnStartUp = (
  settings: Settings,
  vectorTileId: string,
): boolean => {
  const ordering =
    settings['ribbonPlugin.settings']?.layer_ordering?.layers || [];
  return ordering.some(
    (layer: LayerOrder) =>
      layer.type === 'vectortile' && layer.id === vectorTileId,
  );
};

// Helper functions for OpenSeaMap layer
const OPENSEAMAP_LAYER_ID = 'openseamap-seamark';

export const addOpenSeaMapToFavoritesWithOrdering = (settings: Settings) => {
  return addLayerBelowShips(settings, 'openseamap', OPENSEAMAP_LAYER_ID);
};

export const removeOpenSeaMapFromFavoritesWithOrdering = (
  settings: Settings,
) => {
  return removeFromLayerOrdering(settings, OPENSEAMAP_LAYER_ID);
};

export const isOpenSeaMapShownOnStartUp = (settings: Settings): boolean => {
  const ordering =
    settings['ribbonPlugin.settings']?.layer_ordering?.layers || [];
  return ordering.some(
    (layer: LayerOrder) =>
      layer.type === 'openseamap' && layer.id === OPENSEAMAP_LAYER_ID,
  );
};

// Helper functions for Radar Overlay layers
export const addRadarToFavoritesWithOrdering = (
  settings: Settings,
  radarId: string,
) => {
  return addLayerBelowShips(settings, 'cambridge-radar', radarId);
};

export const removeRadarFromFavoritesWithOrdering = (
  settings: Settings,
  radarId: string,
) => {
  return removeFromLayerOrdering(settings, radarId);
};

export const isRadarShownOnStartUp = (
  settings: Settings,
  radarId: string,
): boolean => {
  const ordering =
    settings['ribbonPlugin.settings']?.layer_ordering?.layers || [];
  return ordering.some(
    (layer: LayerOrder) =>
      layer.type === 'cambridge-radar' && layer.id === radarId,
  );
};

// Helper for coverage layers
export const toggleCoverageWithOrdering = (
  settings: Settings,
  enabled: boolean,
) => {
  const updatedSettings = {
    ...settings,
    'ribbonPlugin.settings': {
      ...settings['ribbonPlugin.settings'],
      coverage_source_identifiers: enabled, // Match your actual settings structure
    },
  };

  if (enabled) {
    return addLayerBelowShips(
      updatedSettings,
      'coverage',
      'source-identifiers',
    );
  } else {
    return removeFromLayerOrdering(updatedSettings, 'source-identifiers');
  }
};
